// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import slugify from 'slugify';

// Helpers
import classNames from 'classnames';
import { gtmEventPush } from 'utils/gtm/events';

import Icon from 'Common/Icon';

import styles from 'components/Card.scss';
import Picture from 'Common/Picture';
import Image from 'Common/Image';

import Button from 'Common/Button';

/**
 * <Card /> component.
 * @return {class} returns card instance
 */
class Card extends Component<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      imageSrc: this.props.image,
      showDealerImage: true
    };
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {string} id - card id.
   * @prop {string} name - card name.
   * @prop {number} price - card price.
   * @prop {number} year - card year.
   * @prop {oneOfType} distanceOrTown - card distanceOrTown.
   * @prop {number} mileage - card mileage.
   * @prop {string} fuelType - card fuelType.
   * @prop {string} doors - card doors.
   * @prop {string} transmission - card transmission.
   * @prop {string} image - card image.
   * @prop {object} history - history.
   * @prop {object} item - item.
   * @prop {func} handleClick: handleClick
   */
  static propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    make: PropTypes.string,
    range: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    year: PropTypes.number,
    distanceOrTown: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mileage: PropTypes.number,
    fuelType: PropTypes.string,
    doors: PropTypes.string,
    transmission: PropTypes.string,
    image: PropTypes.object,
    dealerImage: PropTypes.string,
    cardLayout: PropTypes.string,
    history: PropTypes.object,
    item: PropTypes.object,
    handleClick: PropTypes.func,
    network: PropTypes.string,
    gcLid: PropTypes.string,
    gtmSectionName: PropTypes.string.isRequired
  };

  /**
   * Implements defaultProps().
   */
  static defaultProps = {
    cardLayout: 'horizontal'
  };

  setFallbackDealerLogo = () => {
    this.setState({
      showDealerImage: false
    });
  };

  updatedRoute = path => {
    this.props.history.push(path);
  };

  handleClick = e => {
    const { item, handleClick } = this.props;
    e.preventDefault();
    handleClick(item);
    const url = this.generateUrl();
    this.updatedRoute(url);
  };

  handleParentClick = e => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: 'Advert clicked: card'
      }
    });
    this.handleClick(e);
  };

  handleClickChild = e => {
    // Stop the parent onClick from happening too.
    e.stopPropagation();

    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: 'Advert clicked: button'
      }
    });
    this.handleClick(e);
  };

  generateUrl = () => {
    const { id, name, make, range, network, gcLid } = this.props;

    let qsArr = [];
    if (network) {
      qsArr.push(`network=${network}`);
    }

    if (gcLid) {
      qsArr.push(`gclid=${gcLid}`);
    }

    const qs = qsArr.length !== 0 ? '?' + qsArr.join('&') : '';
    return `/used-cars/${slugify(make)}/${slugify(range)}/${slugify(
      name.toLowerCase()
    )}/${id}${qs}`;
  };

  /**
   * Renders <Card /> component.
   *
   * @return {JSX} - renders a logo.
   */
  render() {
    const {
      name,
      price,
      year,
      distanceOrTown,
      mileage,
      fuelType,
      doors,
      transmission,
      dealerImage,
      cardLayout,
      image
    } = this.props;

    const url = this.generateUrl();
    const { card, classCardVertical } = styles;

    return (
      <a
        href={url}
        onClick={this.handleParentClick}
        className={classNames([
          cardLayout === 'vertical' ? classCardVertical : card
        ])}
      >
        {image && (
          <div>
            <Picture pictures={image} />
          </div>
        )}
        {!image && (
          <div>
            <Image
              src={`${process.env.PUBLIC_URL || ''}/images/fallback.png`}
              alt={'Image unavailable'}
              title={'Image unavailable'}
            />
          </div>
        )}
        <div>
          <h3 className={styles.name}>{name}</h3>
          <div className={styles.horizontalDetails}>
            <div className={styles.detailsWrapper}>
              <div className={styles.price}>{price}</div>
              <Button
                extendStyle={styles.button}
                buttonText={'View details'}
                onClick={this.handleClickChild}
              />
            </div>
            <div className={styles.detailsWrapper}>
              <div className={styles.year}>
                <Icon
                  className={classNames(styles.icon)}
                  iconName={'calendar'}
                  size={'medium'}
                />
                {year}
              </div>
              {distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTown
                  )}
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {distanceOrTown}
                </div>
              )}
            </div>
            <div>
              <div className={styles.detailWrapper}>
                <div className={styles.detail} data-mileage>
                  <Icon
                    className={styles.icon}
                    iconName={'road'}
                    size={'medium'}
                  />
                  {mileage && mileage.toLocaleString('en')} Miles
                </div>

                {fuelType && (
                  <div className={styles.detail} data-fuel-type>
                    <Icon
                      className={styles.icon}
                      iconName={'fuel-tank'}
                      size={'medium'}
                    />
                    {fuelType}
                  </div>
                )}
                {transmission && (
                  <div className={styles.detail} data-transmission>
                    <Icon
                      className={styles.icon}
                      iconName={'gearbox'}
                      size={'medium'}
                    />
                    {transmission !== 'N/A'
                      ? transmission.toLowerCase()
                      : transmission}
                  </div>
                )}
                {doors && (
                  <div className={styles.detail} data-doors>
                    <Icon
                      className={styles.icon}
                      iconName={'door'}
                      size={'medium'}
                    />
                    {doors} Doors
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.details}>
            <div>
              <div className={styles.year}>
                <Icon
                  className={classNames(styles.icon)}
                  iconName={'calendar'}
                  size={'medium'}
                />
                {year}
              </div>
              {price && <div className={styles.price}>{price}</div>}

              {distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTownMobile
                  )}
                  data-distance-or-town
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {typeof distanceOrTown === 'string'
                    ? distanceOrTown
                    : distanceOrTown}
                </div>
              )}
              <div className={styles.detailWrapper}>
                <div className={styles.detail} data-mileage>
                  <Icon
                    className={styles.icon}
                    iconName={'road'}
                    size={'medium'}
                  />
                  {mileage && mileage.toLocaleString('en')} Miles
                </div>

                {fuelType && (
                  <div className={styles.detail} data-fuel-type>
                    <Icon
                      className={styles.icon}
                      iconName={'fuel-tank'}
                      size={'medium'}
                    />
                    {fuelType}
                  </div>
                )}
                {transmission && (
                  <div className={styles.detail} data-transmission>
                    <Icon
                      className={styles.icon}
                      iconName={'gearbox'}
                      size={'medium'}
                    />
                    {transmission !== 'N/A'
                      ? transmission.toLowerCase()
                      : transmission}
                  </div>
                )}
                {doors && (
                  <div className={styles.detail} data-doors>
                    <Icon
                      className={styles.icon}
                      iconName={'door'}
                      size={'medium'}
                    />
                    {doors} Doors
                  </div>
                )}
              </div>
            </div>
            <div>
              <Button
                extendStyle={styles.button}
                onClick={this.handleClickChild}
                buttonText={'View details'}
              />
              {distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTown
                  )}
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {distanceOrTown}
                </div>
              )}
              {this.state.showDealerImage && dealerImage && (
                <div className={styles.logoWrapper}>
                  <img
                    className={styles.logo}
                    src={dealerImage}
                    alt="Dealer logo"
                    onError={this.setFallbackDealerLogo}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </a>
    );
  }
}

export default Card;



// WEBPACK FOOTER //
// ./src/Common/Card/index.js